import { create } from 'zustand';
import { appointmentService, Appointment } from '@/services/appointmentService';

interface AppointmentState {
  appointments: Appointment[];
  currentAppointment: Appointment | null;
  loading: boolean;
  error: string | null;
  filters: {
    clientId?: string;
    practiceId?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
  };
  
  // Actions
  fetchAppointments: (params?: any) => Promise<void>;
  fetchAppointment: (id: string) => Promise<void>;
  createAppointment: (data: Partial<Appointment>) => Promise<Appointment>;
  updateAppointment: (id: string, data: Partial<Appointment>) => Promise<void>;
  deleteAppointment: (id: string) => Promise<void>;
  updateStatus: (id: string, status: string) => Promise<void>;
  setFilters: (filters: Partial<AppointmentState['filters']>) => void;
  clearFilters: () => void;
  reset: () => void;
}

const initialState = {
  appointments: [],
  currentAppointment: null,
  loading: false,
  error: null,
  filters: {},
};

export const useAppointmentStore = create<AppointmentState>((set, get) => ({
  ...initialState,

  fetchAppointments: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const { filters } = get();
      const mergedParams = { ...params, ...filters };
      const response = await appointmentService.getAppointments(mergedParams);
      set({
        appointments: response.data?.appointments || response.data || [],
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento degli appuntamenti',
        loading: false,
      });
    }
  },

  fetchAppointment: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await appointmentService.getAppointment(id);
      set({
        currentAppointment: response.data,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento dell\'appuntamento',
        loading: false,
      });
    }
  },

  createAppointment: async (data: Partial<Appointment>) => {
    set({ loading: true, error: null });
    try {
      const response = await appointmentService.createAppointment(data);
      const newAppointment = response.data;
      set((state) => ({
        appointments: [newAppointment, ...state.appointments],
        loading: false,
      }));
      return newAppointment;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Errore nella creazione dell\'appuntamento';
      set({ error: errorMsg, loading: false });
      throw error;
    }
  },

  updateAppointment: async (id: string, data: Partial<Appointment>) => {
    set({ loading: true, error: null });
    try {
      const response = await appointmentService.updateAppointment(id, data);
      const updatedAppointment = response.data;
      set((state) => ({
        appointments: state.appointments.map((a) => (a.id === id ? updatedAppointment : a)),
        currentAppointment: state.currentAppointment?.id === id ? updatedAppointment : state.currentAppointment,
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'aggiornamento dell\'appuntamento',
        loading: false,
      });
      throw error;
    }
  },

  deleteAppointment: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await appointmentService.deleteAppointment(id);
      set((state) => ({
        appointments: state.appointments.filter((a) => a.id !== id),
        currentAppointment: state.currentAppointment?.id === id ? null : state.currentAppointment,
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'eliminazione dell\'appuntamento',
        loading: false,
      });
      throw error;
    }
  },

  updateStatus: async (id: string, status: string) => {
    set({ loading: true, error: null });
    try {
      const response = await appointmentService.updateAppointmentStatus(id, status);
      const updatedAppointment = response.data;
      set((state) => ({
        appointments: state.appointments.map((a) => (a.id === id ? updatedAppointment : a)),
        currentAppointment: state.currentAppointment?.id === id ? updatedAppointment : state.currentAppointment,
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'aggiornamento dello stato',
        loading: false,
      });
      throw error;
    }
  },

  setFilters: (newFilters: Partial<AppointmentState['filters']>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  clearFilters: () => {
    set({ filters: {} });
  },

  reset: () => {
    set(initialState);
  },
}));

